import type { AnimationDB, PrefabCatalog, ProjectData, SpriteDB, SpriteDef, TextureDef } from '../model/gamedata.ts';
import type { PaletteEntry } from '../model/format.ts';

export interface SpriteRef {
  tex: string;
  texDef: TextureDef;
  def: SpriteDef;
}

export interface GameData {
  base: string;
  project: ProjectData;
  sprites: SpriteDB;
  prefabs: PrefabCatalog;
  animations: AnimationDB;
  /** decoded textures; custom tile sheets are canvases drawn in the editor */
  images: Map<string, HTMLImageElement | HTMLCanvasElement>;
  spriteById: Map<string, SpriteRef>;
  spriteByName: Map<string, SpriteRef>;
  sortingIndex: Map<string, number>;
}

let current: GameData | null = null;

export function gameData(): GameData {
  if (!current) throw new Error('game data not loaded');
  return current;
}

async function json<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json();
}

export async function loadGameData(base = '/game-data'): Promise<GameData> {
  if (current) return current;
  const [project, sprites, prefabs, animations] = await Promise.all([
    json<ProjectData>(`${base}/project.json`),
    json<SpriteDB>(`${base}/sprites.json`),
    json<PrefabCatalog>(`${base}/prefabs.json`),
    json<AnimationDB>(`${base}/animations.json`),
  ]);
  const gd: GameData = {
    base,
    project,
    sprites,
    prefabs,
    animations,
    images: new Map(),
    spriteById: new Map(),
    spriteByName: new Map(),
    sortingIndex: new Map(project.sortingLayers.map((n, i) => [n, i])),
  };
  for (const [tex, texDef] of Object.entries(sprites.textures)) {
    for (const def of texDef.sprites) {
      const r: SpriteRef = { tex, texDef, def };
      gd.spriteById.set(`${tex}:${def.id}`, r);
      gd.spriteByName.set(`${tex}|${def.n}`, r);
    }
  }
  await Promise.all(
    Object.keys(sprites.textures).map(
      (guid) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = `${base}/tex/${guid}.png`;
          gd.images.set(guid, img);
        }),
    ),
  );
  current = gd;
  return gd;
}

/** The image when it can be drawn (loaded image or canvas), else null. */
export function readyImage(img: HTMLImageElement | HTMLCanvasElement | undefined): HTMLImageElement | HTMLCanvasElement | null {
  if (!img) return null;
  if ('naturalWidth' in img) return img.complete && img.naturalWidth ? img : null;
  return img;
}

let texVersion = 0;
const texListeners = new Set<() => void>();

/** Increments whenever a texture's pixels change at runtime (custom tiles); drawing caches compare it. */
export function textureVersion(): number {
  return texVersion;
}

export function texturesChanged() {
  texVersion++;
  for (const l of texListeners) l();
}

export function onTexturesChanged(cb: () => void): () => void {
  texListeners.add(cb);
  return () => texListeners.delete(cb);
}

/** Install or replace a runtime texture (custom tile sheet) with its sprites. */
export function registerTexture(guid: string, def: TextureDef, image: HTMLCanvasElement) {
  const gd = gameData();
  const old = gd.sprites.textures[guid];
  if (old)
    for (const s of old.sprites) {
      gd.spriteById.delete(`${guid}:${s.id}`);
      gd.spriteByName.delete(`${guid}|${s.n}`);
    }
  gd.sprites.textures[guid] = def;
  for (const s of def.sprites) {
    const r: SpriteRef = { tex: guid, texDef: def, def: s };
    gd.spriteById.set(`${guid}:${s.id}`, r);
    gd.spriteByName.set(`${guid}|${s.n}`, r);
  }
  gd.images.set(guid, image);
  texturesChanged();
}

/** Resolve a Unity sprite reference ({fileID, guid}) */
export function spriteFromRef(r: any): SpriteRef | null {
  if (!r || !r.guid || !current) return null;
  const tex = current.sprites.textures[r.guid];
  if (!tex) return null;
  return current.spriteById.get(`${r.guid}:${r.fileID}`) ?? (tex.sprites.length === 1 ? { tex: r.guid, texDef: tex, def: tex.sprites[0] } : null);
}

export function spriteByName(tex: string, name: string): SpriteRef | null {
  return current?.spriteByName.get(`${tex}|${name}`) ?? null;
}

export function paletteSprite(e: PaletteEntry | undefined): SpriteRef | null {
  if (!e) return null;
  return spriteByName(e.tex, e.sprite);
}

/** Collider type of a palette entry: 0 none, 1 sprite shape, 2 full grid cell. */
export function paletteCollider(e: PaletteEntry | undefined): number {
  if (!e) return 0;
  if (e.tile) return current?.sprites.tiles[e.tile]?.collider ?? 1;
  return 1;
}

export function sortingIndex(name: string): number {
  return current?.sortingIndex.get(name) ?? current?.sortingIndex.get('Default') ?? 0;
}

export function sortingLayerNameFromId(id: any): string {
  if (!current) return 'Default';
  const n = (BigInt(String(id ?? '0')) & 0xffffffffn).toString();
  return current.project.sortingLayerIds[n] ?? 'Default';
}

/** Sort key combining sorting layer and order, used for draw ordering. */
export function sortKey(layer: string, order: number): number {
  return sortingIndex(layer) * 100000 + Math.max(-49999, Math.min(49999, order));
}
